import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Users, FileText, Lock, Tag, Menu, X, Phone, Mail, MapPin, Instagram, Image, StickyNote, Settings as SettingsIcon } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useSearch } from '../contexts/SearchContext';
import NotesSidebar from './NotesSidebar';
import DashboardHeader from './DashboardHeader';

interface LayoutProps {
	children: React.ReactNode;
}

type Module = 'password-manager' | 'stalker' | 'dashboard' | 'notes';

// High-Fidelity FUI Globe Logo (Dribbble-inspired Matrix Emerald)
const GlobeLogo: React.FC<{ isDark: boolean }> = ({ isDark }) => {
	const c = isDark ? '#10b981' : '#059669';
	const cGlow = isDark ? '#10b98144' : '#05966944';

	return (
		<div className="relative w-10 h-10 flex items-center justify-center group overflow-hidden">
			<style>{`
				@keyframes globe-rotate {
					0% { transform: rotateY(0deg) rotateX(15deg); }
					100% { transform: rotateY(360deg) rotateX(15deg); }
				}
				@keyframes scan-move {
					0%, 100% { transform: translateY(-18px) scaleX(1); opacity: 0; }
					50% { transform: translateY(0px) scaleX(1.1); opacity: 0.8; }
					100% { transform: translateY(18px) scaleX(1); opacity: 0; }
				}
				@keyframes arc-travel {
					0% { stroke-dashoffset: 50; opacity: 0; }
					20% { opacity: 1; }
					80% { opacity: 1; }
					100% { stroke-dashoffset: -50; opacity: 0; }
				}
				@keyframes pulse-node {
					0%, 100% { transform: scale(1); opacity: 0.5; }
					50% { transform: scale(1.5); opacity: 1; }
				}
				.globe-3d-wrap {
					transform-style: preserve-3d;
					perspective: 800px;
					animation: globe-rotate 15s linear infinite;
				}
				.scan-ring {
					animation: scan-move 4s ease-in-out infinite;
				}
				.comet-arc {
					stroke-dasharray: 10 40;
					animation: arc-travel 3s linear infinite;
				}
			`}</style>

			<svg viewBox="0 0 44 44" width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg">
				<defs>
					{/* Atmosphere Glow */}
					<radialGradient id="rim-glow" cx="50%" cy="50%" r="50%">
						<stop offset="85%" stopColor="transparent" />
						<stop offset="100%" stopColor={c} stopOpacity="0.4" />
					</radialGradient>
					{/* Dot Pattern for Continents */}
					<pattern id="dot-pattern" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
						<circle cx="1" cy="1" r="0.6" fill={c} fillOpacity="0.4" />
					</pattern>
					{/* Simplified Continent Mask Paths */}
					<mask id="world-mask">
						<circle cx="22" cy="22" r="18" fill="white" fillOpacity="0.1" />
						<path d="M10 18 C 12 15, 15 15, 18 18 S 22 22, 25 18 S 30 15, 34 18" stroke="white" strokeWidth="6" fill="none" opacity="0.8" />
						<path d="M12 25 C 15 28, 20 28, 25 25 S 32 22, 36 26" stroke="white" strokeWidth="5" fill="none" opacity="0.8" />
						<path d="M22 10 C 24 12, 24 16, 22 20 S 18 24, 20 28" stroke="white" strokeWidth="4" fill="none" opacity="0.7" />
					</mask>
				</defs>

				{/* Atmosphere / Outer Shell */}
				<circle cx="22" cy="22" r="20" stroke={c} strokeWidth="0.5" strokeDasharray="2 4" opacity="0.2" />
				<circle cx="22" cy="22" r="18" fill="url(#rim-glow)" />

				<g className="globe-3d-wrap">
					{/* The Dotted Sphere Surface */}
					<circle cx="22" cy="22" r="18" fill="url(#dot-pattern)" mask="url(#world-mask)" />
					
					{/* Longitude / Latitude Skeleton */}
					<circle cx="22" cy="22" r="18" stroke={c} strokeWidth="0.2" opacity="0.15" />
					<ellipse cx="22" cy="22" rx="18" ry="6" stroke={c} strokeWidth="0.2" opacity="0.15" />
					<ellipse cx="22" cy="22" rx="6" ry="18" stroke={c} strokeWidth="0.2" opacity="0.15" />

					{/* Connection Arcs (Comets) - Multi-layered for continuity */}
					<path d="M12 28 Q 22 10 32 28" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '0s' }} />
					<path d="M22 8 Q 38 22 22 36" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '1.5s' }} />
					<path d="M8 22 Q 22 36 36 22" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '3s' }} />
					<path d="M12 28 Q 22 10 32 28" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '4.5s' }} />
					<path d="M30 12 Q 15 22 30 32" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '0.75s' }} />
					<path d="M10 15 Q 30 15 34 30" stroke={c} strokeWidth="1" className="comet-arc" style={{ animationDelay: '2.25s' }} />

					{/* Pulsing Nodes */}
					<circle cx="12" cy="28" r="1.5" fill={c} className="animate-[pulse-node_2s_infinite]" />
					<circle cx="32" cy="28" r="1.5" fill={c} className="animate-[pulse-node_2s_infinite_1s]" />
					<circle cx="22" cy="8" r="1.5" fill={c} className="animate-[pulse-node_3s_infinite]" />
					<circle cx="30" cy="12" r="1.2" fill={c} className="animate-[pulse-node_2.5s_infinite_0.5s]" />
				</g>

				{/* Vertical Scanning Ring */}
				<ellipse cx="22" cy="22" rx="19" ry="2" stroke={c} strokeWidth="0.8" className="scan-ring" />
			</svg>
		</div>
	);
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const { isDark, toggleDarkMode } = useDarkMode();
	const { globalSearchTerm, setGlobalSearchTerm } = useSearch();
	const navigate = useNavigate();
	const location = useLocation();

	const [currentModule, setCurrentModule] = useState<Module>(() => {
		if (location.pathname.startsWith('/password-manager')) return 'password-manager';
		if (location.pathname.startsWith('/notes')) return 'notes';
		if (location.pathname.startsWith('/stalker')) return 'stalker';
		return 'dashboard';
	});

	const isDashboard = currentModule === 'dashboard';

	const [sidebarOpen, setSidebarOpen] = useState(false);

	useEffect(() => {
		if (location.pathname.startsWith('/password-manager')) {
			setCurrentModule('password-manager');
		} else if (location.pathname.startsWith('/notes')) {
			setCurrentModule('notes');
		} else if (location.pathname.startsWith('/stalker')) {
			setCurrentModule('stalker');
		} else if (location.pathname.startsWith('/dashboard')) {
			setCurrentModule('dashboard');
		}
		// When navigating to /settings, close the inner menu sidebar by switching to dashboard module context
		if (location.pathname === '/settings') {
			setCurrentModule('dashboard');
			setSidebarOpen(false);
		}
	}, [location.pathname]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		console.log('Global search:', globalSearchTerm);
	};

	if (!isAuthenticated) {
		return <>{children}</>;
	}

	// Password Manager menu items
	const passwordManagerMenu = [
		{ path: '/password-manager/passwords', label: 'Passwords', icon: Lock },
		{ path: '/password-manager/tags', label: 'Tags', icon: Tag },
		{ path: '/password-manager/logs', label: 'Logs', icon: FileText },
	];

	// Stalker menu items
	const stalkerMenu = [
		{ path: '/stalker/persons', label: 'Persons', icon: Users },
		{ path: '/stalker/numbers', label: 'Numbers', icon: Phone },
		{ path: '/stalker/emails', label: 'Emails', icon: Mail },
		{ path: '/stalker/locations', label: 'Locations', icon: MapPin },
		{ path: '/stalker/socials', label: 'Socials', icon: Instagram },
		{ path: '/stalker/medias', label: 'Medias', icon: Image },
		{ path: '/stalker/logs', label: 'Logs', icon: FileText },
		{ path: '/stalker/settings', label: 'Settings', icon: SettingsIcon },
	];

	const currentMenu = currentModule === 'password-manager' ? passwordManagerMenu : currentModule === 'notes' ? [] : stalkerMenu;

	// Whether the inner menu sidebar should show
	const hasInnerSidebar = currentModule !== 'dashboard';

	return (
		<div className={`min-h-screen flex ${isDark ? 'bg-[#0a0a0a]' : 'bg-neutral-50'}`}>
			{/* Mobile Overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden"
					style={{ zIndex: 40 }}
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Sidebar - Icon Bar */}
			<div 
				className={`fixed top-0 h-screen left-0 transition-transform duration-300 w-20 lg:w-16 px-3 lg:px-0 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'} border-r ${isDark ? 'border-[#222]' : 'border-neutral-200'} flex flex-col justify-between py-4 sidebar-icon-bar`} 
				style={{ 
					zIndex: 999, 
					transform: sidebarOpen ? 'translateX(0)' : 'translateX(-135%)'
				}}
			>
				{/* Logo */}
				<div className="flex items-center justify-center">
					<Link to="/dashboard" title="Dashboard" onClick={() => setCurrentModule('dashboard')}>
						<GlobeLogo isDark={isDark} />
					</Link>
				</div>

				{/* Modules */}
				<div className="flex flex-col items-center space-y-4 font-mono">
					<Link
						to="/dashboard"
						onClick={() => { setCurrentModule('dashboard'); if (window.innerWidth < 1024) setSidebarOpen(false); }}
						data-tooltip-id="module-tooltip"
						data-tooltip-content="Dashboard"
						className={`p-3 transition-colors rounded-none border-l-2 ${
							currentModule === 'dashboard'
								? isDark ? 'border-emerald-500 bg-[#1a1a1a] text-emerald-400' : 'border-emerald-600 bg-emerald-50 text-emerald-700'
								: isDark ? 'border-transparent hover:bg-[#111] text-neutral-500' : 'border-transparent hover:bg-neutral-100 text-neutral-500'
						}`}
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
						</svg>
					</Link>
					<Link
						to="/password-manager/passwords"
						onClick={() => { setCurrentModule('password-manager'); }}
						data-tooltip-id="module-tooltip"
						data-tooltip-content="Password Manager"
						className={`p-3 transition-colors rounded-none border-l-2 ${
							currentModule === 'password-manager'
								? isDark ? 'border-cyan-500 bg-[#1a1a1a] text-cyan-400' : 'border-cyan-600 bg-cyan-50 text-cyan-700'
								: isDark ? 'border-transparent hover:bg-[#111] text-neutral-500' : 'border-transparent hover:bg-neutral-100 text-neutral-500'
						}`}
					>
						<Lock className="w-5 h-5" strokeWidth={1.5} />
					</Link>
					<Link
						to="/notes"
						onClick={() => { setCurrentModule('notes'); }}
						data-tooltip-id="module-tooltip"
						data-tooltip-content="Notes"
						className={`p-3 transition-colors rounded-none border-l-2 ${
							currentModule === 'notes'
								? isDark ? 'border-yellow-500 bg-[#1a1a1a] text-yellow-400' : 'border-yellow-600 bg-yellow-50 text-yellow-700'
								: isDark ? 'border-transparent hover:bg-[#111] text-neutral-500' : 'border-transparent hover:bg-neutral-100 text-neutral-500'
						}`}
					>
						<StickyNote className="w-5 h-5" strokeWidth={1.5} />
					</Link>
					<Link
						to="/stalker/persons"
						onClick={() => { setCurrentModule('stalker'); }}
						data-tooltip-id="module-tooltip"
						data-tooltip-content="Stalker"
						className={`p-3 transition-colors rounded-none border-l-2 ${
							currentModule === 'stalker'
								? isDark ? 'border-indigo-500 bg-[#1a1a1a] text-indigo-400' : 'border-indigo-600 bg-indigo-50 text-indigo-700'
								: isDark ? 'border-transparent hover:bg-[#111] text-neutral-500' : 'border-transparent hover:bg-neutral-100 text-neutral-500'
						}`}
					>
						<Users className="w-5 h-5" strokeWidth={1.5} />
					</Link>
				</div>

				{/* User Container */}
				<div className="flex flex-col items-center space-y-4">
					<button
						onClick={toggleDarkMode}
						data-tooltip-id="module-tooltip"
						data-tooltip-content={isDark ? 'Light Mode' : 'Dark Mode'}
						className={`p-3 transition-colors ${isDark ? 'hover:text-white text-neutral-500' : 'hover:text-black text-neutral-400'}`}
					>
						{isDark ? (
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
							</svg>
						) : (
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
							</svg>
						)}
					</button>
					<button
						onClick={() => {
							navigate('/settings');
							setSidebarOpen(false);
						}}
						data-tooltip-id="module-tooltip"
						data-tooltip-content="Settings"
						className={`w-8 h-8 flex items-center justify-center focus:outline-none border ${isDark ? 'bg-[#111] border-[#333] text-emerald-500' : 'bg-neutral-100 border-neutral-300 text-emerald-700'}`}
					>
						<User className="w-4 h-4" />
					</button>
				</div>

				<Tooltip id="module-tooltip" place="right" style={{ zIndex: 10000 }} offset={8} />
			</div>

			{/* Sidebar - Inner Menu Bar */}
			{hasInnerSidebar && (
				<div 
					className={`fixed top-0 h-screen left-20 lg:left-16 transition-transform duration-300 w-64 ${isDark ? 'bg-[#050505]' : 'bg-neutral-50'} border-r ${isDark ? 'border-[#222]' : 'border-neutral-200'} flex flex-col sidebar-menu-bar`} 
					style={{ 
						zIndex: 99, 
						transform: sidebarOpen ? 'translateX(0)' : 'translateX(-135%)'
					}}
				>
					<div className={`h-[60px] flex items-center border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'} pl-4`}>
						<h2 className={`text-xs font-mono font-semibold tracking-widest uppercase ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
							{currentModule === 'password-manager' ? 'SEC.VAULT' : currentModule === 'notes' ? 'SEC.NOTES' : 'TARGET.INTEL'}
						</h2>
					</div>
					<nav className="flex-1 p-3 space-y-1 overflow-y-auto">
						{currentModule === 'notes' ? (
							<NotesSidebar isDark={isDark} location={location} onItemClick={() => {
								if (window.innerWidth < 1024) { setSidebarOpen(false); }
							}} />
						) : (
							currentMenu.map((item) => {
								const Icon = item.icon;
								const isActive = location.pathname === item.path || 
									(item.path === '/stalker/persons' && location.pathname.startsWith('/stalker/persons/')) ||
									(item.path === '/stalker/socials' && location.pathname.startsWith('/stalker/socials/'));
								return (
									<Link
										key={item.path}
										to={item.path}
										onClick={() => {
											if (window.innerWidth < 1024) { setSidebarOpen(false); }
										}}
										className={`flex items-center space-x-3 px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-l-2 ${
											isActive
												? isDark ? 'bg-[#111] text-indigo-400 border-indigo-500' : 'bg-indigo-50 text-indigo-700 border-indigo-600'
												: isDark ? 'border-transparent text-neutral-500 hover:bg-[#111] hover:text-neutral-300' : 'border-transparent text-neutral-600 hover:bg-neutral-100'
										}`}
									>
										<Icon className="w-4 h-4" />
										<span>{item.label}</span>
									</Link>
								);
							})
						)}
					</nav>
				</div>
			)}

			{/* Main Content Area */}
			<div className={`flex-1 flex flex-col min-w-0 overflow-x-hidden ${isDashboard ? 'lg:ml-[64px]' : 'lg:ml-[320px]'}`}>
				{/* Top Bar */}
				<header className={`sticky top-0 z-30 h-[60px] flex items-center ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'} border-b`}>
					<div className="px-2 lg:px-4 py-2 w-full flex items-center gap-4">
						{/* Mobile Hamburger Button */}
						<button
							onClick={() => setSidebarOpen(!sidebarOpen)}
							className={`lg:hidden p-2 rounded-sm ${isDark ? 'hover:bg-[#111] text-neutral-400' : 'hover:bg-neutral-200 text-neutral-500'}`}
						>
							{sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
						</button>

						{/* Search bar (non-dashboard pages) */}
						{!isDashboard && (
							<form onSubmit={handleSearch} className="flex-1 max-w-2xl">
								<div className="relative flex items-center">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<Search className={`h-4 w-4 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
									</div>
									<input
										type="text"
										placeholder="QUERY_DB..."
										className={`block w-full pl-9 pr-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-none focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
											isDark
												? 'bg-[#111] border-[#333] text-neutral-200 placeholder-neutral-600 focus:border-emerald-500'
												: 'bg-white border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:border-emerald-500'
										}`}
										value={globalSearchTerm}
										onChange={(e) => setGlobalSearchTerm(e.target.value)}
									/>
								</div>
							</form>
						)}

						{/* Clock bar: only visible when on dashboard */}
						<div className={`${isDashboard ? 'flex' : 'hidden'} ml-auto flex-1 justify-end min-w-0`}>
							<DashboardHeader isDark={isDark} />
						</div>
					</div>
				</header>

				{/* Main Content */}
				<main className="flex-1 overflow-y-auto p-2 md:p-6 max-w-full md:max-w-none" style={{ maxWidth: '100vw' }}>
					{children}
				</main>
			</div>
		</div>
	);
};

export default Layout;
